import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_design-breakthrough/artifacts/tyi8grfo_Original%20on%20transparent.webp";

const serviceLinks = [
  { label: "Video Production", to: "/services/video-production" },
  { label: "Artist Showreels", to: "/services/artist-showreels" },
  { label: "Business Content & Promo", to: "/services/business-promos" },
  { label: "Event Production", to: "/services/event-production" },
  { label: "Dance & Theatre Shows", to: "/services/dance-shows" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const linkCls = "link-underline text-sm uppercase tracking-[0.2em] font-medium text-white transition-colors duration-300";

  return (
    <>
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={LOGO} alt="WVE Studios" className="h-11 sm:h-14 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/work" data-testid="nav-link-work" className={`${linkCls} ${pathname === "/work" ? "text-[#E80042]" : ""}`}>
            Work
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <Link to="/services" data-testid="nav-link-services" className={`${linkCls} inline-flex items-center gap-1.5 ${pathname === "/services" ? "text-[#E80042]" : ""}`}>
              Services
              <span className={`text-[10px] transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`}>▼</span>
            </Link>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                  data-testid="services-dropdown"
                >
                  <div className="bg-[#0A0A0A] border border-white/15 rounded-lg shadow-xl py-3 min-w-[250px]">
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.to}
                        to={s.to}
                        data-testid={`services-dropdown-${s.to.split("/").pop()}`}
                        className="block px-6 py-2.5 text-sm uppercase tracking-[0.12em] font-medium text-white hover:text-[#E80042] hover:translate-x-1 transition-[color,transform] duration-200"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/studios" data-testid="nav-link-studios" className={`${linkCls} ${pathname === "/studios" ? "text-[#E80042]" : ""}`}>
            Studios
          </Link>
          <Link to="/live" data-testid="nav-link-live" className={`${linkCls} ${pathname === "/live" ? "text-[#E80042]" : ""}`}>
            Live
          </Link>
          <Link to="/#team" data-testid="nav-link-team" className={linkCls}>
            Team
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="bg-[#E80042] text-white text-sm uppercase tracking-[0.15em] font-semibold px-5 sm:px-7 py-2.5 sm:py-3 rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300 active:scale-95"
          >
            Let's Talk
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            className="md:hidden w-11 h-11 flex flex-col items-center justify-center gap-1.5"
          >
            <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block w-6 h-0.5 bg-white transition-opacity duration-300 ${mobileOpen ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </div>
    </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden fixed inset-0 top-16 sm:top-20 bg-[#0A0A0A] z-40 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-10 flex flex-col gap-1">
              {[
                { label: "Work", to: "/work" },
                { label: "Services", to: "/services" },
                { label: "Studios", to: "/studios" },
                { label: "Live", to: "/live" },
                { label: "Team", to: "/#team" },
                { label: "Contact", to: "/contact" },
              ].map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    onClick={() => setMobileOpen(false)}
                    className="block font-display uppercase text-4xl text-white py-3 border-b border-white/10 hover:text-[#E80042] transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-6">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3">All Services</p>
                <div className="flex flex-col gap-2">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      data-testid={`mobile-service-${s.to.split("/").pop()}`}
                      onClick={() => setMobileOpen(false)}
                      className="text-white/80 uppercase tracking-[0.12em] text-sm py-1.5 hover:text-[#E80042] transition-colors duration-200"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
                <a href="tel:01277280512" className="inline-block mt-8 border border-white/30 text-white font-grotesk font-bold px-7 py-3.5 rounded-full" data-testid="mobile-phone-link">
                  Call 01277 280 512
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
